<template>
  <div class="stats-page">

    <!-- ────────────────────── HEADER ────────────────────── -->
    <header class="page-header">
      <div class="header-inner">
        <div class="header-title-row">
          <h1 class="page-title">דף ראשי</h1>
          <span class="page-date">{{ todayHebrew }}</span>
        </div>
        <button class="refresh-btn" @click="getDetails" :disabled="progressShow" aria-label="רענן נתונים">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"
            :class="{ 'is-spinning': progressShow }">
            <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
          </svg>
          <span>רענן</span>
        </button>
      </div>
    </header>

    <ProgressShow v-if="progressShow" v-model="progressShow"></ProgressShow>

    <!-- ────────────────────── CONTENT ────────────────────── -->
    <main class="page-content">

      <!-- KPI CARDS -->
      <section class="kpi-grid" aria-label="מדדים עיקריים">

        <article class="kpi-card" data-accent="navy">
          <div class="kpi-card__bar"></div>
          <div class="kpi-card__head">
            <div class="kpi-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <span class="kpi-tag">סך הכל</span>
          </div>
          <div class="kpi-label">לקוחות</div>
          <div class="kpi-value">{{ formatNum(customerCounts) }}</div>
        </article>

        <article class="kpi-card" data-accent="indigo">
          <div class="kpi-card__bar"></div>
          <div class="kpi-card__head">
            <div class="kpi-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
            </div>
            <span class="kpi-tag">פעיל</span>
          </div>
          <div class="kpi-label">חיבורים (בתהליך / מחוברים)</div>
          <div class="kpi-value">
            <span class="kpi-pair">{{ formatNum(manuimInProcessCounts) }}</span>
            <span class="kpi-divider">/</span>
            <span class="kpi-pair">{{ formatNum(manuimActiveCounts) }}</span>
          </div>
        </article>

        <article class="kpi-card" data-accent="purple">
          <div class="kpi-card__bar"></div>
          <div class="kpi-card__head">
            <div class="kpi-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
              </svg>
            </div>
            <span class="kpi-tag">היסטורי</span>
          </div>
          <div class="kpi-label">ניודים החוצה</div>
          <div class="kpi-value">{{ formatNum(manuimNotActiveCounts) }}</div>
        </article>

        <article class="kpi-card" data-accent="gold">
          <div class="kpi-card__bar"></div>
          <div class="kpi-card__head">
            <div class="kpi-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <line x1="19" y1="8" x2="19" y2="14"></line>
                <line x1="22" y1="11" x2="16" y2="11"></line>
              </svg>
            </div>
            <span class="kpi-tag">היום</span>
          </div>
          <div class="kpi-label">לקוחות חדשים</div>
          <div class="kpi-value">{{ formatNum(customersFromToday) }}</div>
        </article>

        <article class="kpi-card" data-accent="sky">
          <div class="kpi-card__bar"></div>
          <div class="kpi-card__head">
            <div class="kpi-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="16"></line>
                <line x1="8" y1="12" x2="16" y2="12"></line>
              </svg>
            </div>
            <span class="kpi-tag">היום</span>
          </div>
          <div class="kpi-label">חיבורים חדשים</div>
          <div class="kpi-value">{{ formatNum(manuimFromToday) }}</div>
        </article>

      </section>

      <!-- CHART SECTION -->
      <section class="chart-card" aria-label="מגמות לאורך זמן">
        <div class="chart-card__head">
          <div>
            <div class="chart-eyebrow">ניתוח מגמות</div>
            <h2 class="chart-title">פעילות לאורך זמן</h2>
          </div>
          <div class="chart-period">
            <span class="period-dot"></span>
            <span>15 הימים האחרונים</span>
          </div>
        </div>
        <div class="chart-canvas-wrap">
          <canvas id="myChart"></canvas>
        </div>
      </section>

    </main>
  </div>
</template>

<script>
import SnackBar from '@/components/widgets/snackBar.vue'
import Chart from 'chart.js/auto';
import ApiServices from '@/services/api.service'
import ProgressShow from '@/components/widgets/progressShow.vue'

export default {
  components: {
    SnackBar,
    ProgressShow
  },
  data: () => ({
    manuimActiveCounts: 0,
    manuimInProcessCounts: 0,
    manuimNotActiveCounts: 0,
    customerCounts: 0,
    customersFromToday: 0,
    manuimFromToday: 0,
    progressShow: false,
    chartInstance: null,
  }),
  computed: {
    // Hebrew formatted current date (e.g. "יום ראשון, 8 ביוני 2026")
    todayHebrew() {
      try {
        return new Date().toLocaleDateString('he-IL', {
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
        });
      } catch (e) {
        return '';
      }
    },
  },
  methods: {
    // Pretty number formatting with thousand separators (Hebrew locale)
    formatNum(n) {
      const v = Number(n);
      if (!isFinite(v)) return '0';
      return v.toLocaleString('he-IL');
    },
    async getDetails() {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + `/statistics/get_details`;
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const jsonObject = await res.json();

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בקבלת נתונים", "red");
        } else if (res.status === 200) {
          console.log(jsonObject);

          this.customersFromToday = jsonObject.customersFromToday;
          this.manuimFromToday = jsonObject.manuimFromToday;
          this.customerCounts = jsonObject.totalCustomers;
          this.manuimActiveCounts = jsonObject.totalActiveManuim;
          this.manuimInProcessCounts = jsonObject.totalProcessManuim;
          this.manuimNotActiveCounts = jsonObject.totalManuimNotActive;
          this.initializeChart(jsonObject.labels, jsonObject.datasets);
          this.progressShow = false;
        }
      } catch (error) {
        console.log(error);
        this.showSnackBar("Error get files: " + error, "red");
      }
    },
    initializeChart(labels, datasets) {
      // Brand-aligned palette: navy, indigo, gold, purple
      const palette = [
        { border: '#0d2c6d', bg: 'rgba(13, 44, 109, 0.08)' },
        { border: '#3F51B5', bg: 'rgba(63, 81, 181, 0.08)' },
        { border: '#fdb721', bg: 'rgba(253, 183, 33, 0.10)' },
        { border: '#8066f0', bg: 'rgba(128, 102, 240, 0.08)' },
      ];

      const enhancedDatasets = datasets.map((dataset, index) => {
        const color = palette[index % palette.length];
        return {
          ...dataset,
          borderColor: color.border,
          backgroundColor: color.bg,
          borderWidth: 2.5,
          tension: 0.4,
          fill: true,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointBackgroundColor: '#fff',
          pointBorderColor: color.border,
          pointBorderWidth: 2,
          pointHoverBackgroundColor: color.border,
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
        };
      });

      const data = { labels, datasets: enhancedDatasets };

      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const ctx = document.getElementById('myChart');
      this.chartInstance = new Chart(ctx, {
        type: 'line',
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              align: 'end',
              labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 18,
                font: { size: 12, family: 'Assistant, Heebo, system-ui, sans-serif', weight: '600' },
                color: '#475569'
              }
            },
            tooltip: {
              backgroundColor: 'rgba(13, 44, 109, 0.96)',
              padding: 12,
              cornerRadius: 10,
              titleFont: { size: 13, weight: 'bold', family: 'Assistant, Heebo, system-ui, sans-serif' },
              bodyFont: { size: 13, family: 'Assistant, Heebo, system-ui, sans-serif' },
              titleColor: '#fdb721',
              bodyColor: '#ffffff',
              displayColors: true,
              boxPadding: 6,
              borderColor: 'rgba(255,255,255,0.08)',
              borderWidth: 1
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1, precision: 0,
                font: { size: 12, family: 'Assistant, Heebo, system-ui, sans-serif' },
                color: '#94a3b8',
                padding: 8
              },
              grid: { color: 'rgba(13, 44, 109, 0.06)', drawBorder: false }
            },
            x: {
              ticks: {
                font: { size: 11, family: 'Assistant, Heebo, system-ui, sans-serif' },
                color: '#94a3b8',
                maxRotation: 45, minRotation: 45,
                padding: 6
              },
              grid: { display: false, drawBorder: false }
            }
          }
        }
      });
    },
    // Helper function to count entries by date
    countByDate(entries) {
      const counts = {};
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 14); // 15 days including today

      for (let i = 0; i < 15; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const formattedDate = this.formatDate(date);
        counts[formattedDate] = 0;
      }

      entries.forEach(entry => {
        const entryDate = new Date(entry.createdAt);
        const formattedEntryDate = this.formatDate(entryDate);
        if (formattedEntryDate in counts) {
          counts[formattedEntryDate]++;
        }
      });

      return counts;
    },
    formatDate(d) {
      let month = '' + (d.getMonth() + 1);
      let day = '' + d.getDate();
      let year = d.getFullYear();

      if (month.length < 2) month = '0' + month;
      if (day.length < 2) day = '0' + day;

      return [day, month, year].join('-');
    },
    todatyCount(data) {
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, '0');
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const yyyy = today.getFullYear();
      const formattedToday = dd + '-' + mm + '-' + yyyy;
      return data[formattedToday] || 0;
    },
    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true
    },
  },
  mounted() {
    this.getDetails();
  },
}
</script>

<style scoped>
/* ───────────────────────── BASE & TOKENS ───────────────────────── */
.stats-page {
  --c-bg: #f5f7fa;
  --c-surface: #ffffff;
  --c-navy: #0d2c6d;
  --c-navy-deep: #081f54;
  --c-indigo: #3F51B5;
  --c-blue: #1976d2;
  --c-sky: #42a5f5;
  --c-gold: #fdb721;
  --c-purple: #8066f0;
  --c-text: #0f172a;
  --c-text-soft: #475569;
  --c-text-mute: #94a3b8;
  --c-border: #e2e8f0;
  --c-border-soft: #eef2f7;

  --shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
  --shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
  --shadow-lg: 0 18px 40px -12px rgba(13, 44, 109, 0.18), 0 8px 16px -8px rgba(13, 44, 109, 0.10);

  --ease: cubic-bezier(0.22, 1, 0.36, 1);

  font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  direction: rtl;
  background: var(--c-bg);
  min-height: 100vh;
  color: var(--c-text);
}

/* ───────────────────────── HEADER ───────────────────────── */
/* ───────────────────────── HEADER ───────────────────────── */
.page-header {
  background: linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
  color: #fff;
  padding: 12px 0 14px;
  box-shadow: var(--shadow-md);
}

.header-inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.header-title-row {
  display: flex;
  align-items: baseline;
  gap: 14px;
  min-width: 0;
}

.page-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.page-date {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 500;
  white-space: nowrap;
}

.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s var(--ease);
  flex-shrink: 0;
}

.refresh-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.35);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-btn svg {
  transition: transform 0.6s var(--ease);
}

.refresh-btn svg.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(-360deg);
  }
}

/* RTL-friendly direction */

/* ───────────────────────── CONTENT ───────────────────────── */
.page-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 28px 32px 48px;
}

/* ───────────────────────── KPI GRID ───────────────────────── */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
  margin-bottom: 28px;
}

.kpi-card {
  position: relative;
  background: var(--c-surface);
  border-radius: 16px;
  padding: 20px 22px 22px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--c-border-soft);
  overflow: hidden;
  transition: transform 0.35s var(--ease), box-shadow 0.35s var(--ease), border-color 0.35s var(--ease);
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: cardIn 0.55s var(--ease) backwards;
}

.kpi-card:nth-child(1) {
  animation-delay: 0.00s;
}

.kpi-card:nth-child(2) {
  animation-delay: 0.06s;
}

.kpi-card:nth-child(3) {
  animation-delay: 0.12s;
}

.kpi-card:nth-child(4) {
  animation-delay: 0.18s;
}

.kpi-card:nth-child(5) {
  animation-delay: 0.24s;
}

@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-lg);
  border-color: transparent;
}

/* Right-edge accent strip (RTL leading edge) */
.kpi-card__bar {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 3px;
  background: var(--accent);
  opacity: 0.85;
}

/* Soft background flourish in top-left corner */
.kpi-card::after {
  content: '';
  position: absolute;
  top: -40px;
  left: -40px;
  width: 140px;
  height: 140px;
  background: radial-gradient(circle, var(--accent-soft) 0%, transparent 70%);
  opacity: 0.6;
  pointer-events: none;
  transition: opacity 0.35s var(--ease);
}

.kpi-card:hover::after {
  opacity: 1;
}

.kpi-card[data-accent="navy"] {
  --accent: var(--c-navy);
  --accent-soft: rgba(13, 44, 109, 0.18);
}

.kpi-card[data-accent="indigo"] {
  --accent: var(--c-indigo);
  --accent-soft: rgba(63, 81, 181, 0.16);
}

.kpi-card[data-accent="purple"] {
  --accent: var(--c-purple);
  --accent-soft: rgba(128, 102, 240, 0.16);
}

.kpi-card[data-accent="gold"] {
  --accent: var(--c-gold);
  --accent-soft: rgba(253, 183, 33, 0.22);
}

.kpi-card[data-accent="sky"] {
  --accent: var(--c-blue);
  --accent-soft: rgba(25, 118, 210, 0.16);
}

.kpi-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.kpi-icon {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: var(--accent);
  color: #fff;
  box-shadow: 0 6px 14px -4px var(--accent-soft);
  transition: transform 0.35s var(--ease);
}

.kpi-card:hover .kpi-icon {
  transform: rotate(-6deg) scale(1.06);
}

.kpi-card[data-accent="gold"] .kpi-icon {
  color: #4a3500;
}

.kpi-tag {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
}

.kpi-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--c-text-soft);
  line-height: 1.35;
}

.kpi-value {
  font-size: 2.15rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--c-text);
  line-height: 1.1;
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-feature-settings: "tnum" 1;
  /* tabular numerals — values align nicely */
}

.kpi-pair {
  color: var(--c-text);
}

.kpi-divider {
  font-size: 1.4rem;
  font-weight: 500;
  color: var(--c-text-mute);
  margin: 0 2px;
}

/* ───────────────────────── CHART ───────────────────────── */
.chart-card {
  background: var(--c-surface);
  border-radius: 18px;
  border: 1px solid var(--c-border-soft);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  animation: cardIn 0.55s var(--ease) 0.30s backwards;
}

.chart-card__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 22px 26px 18px;
  border-bottom: 1px solid var(--c-border-soft);
  background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
  gap: 16px;
}

.chart-eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: var(--c-purple);
  margin-bottom: 4px;
}

.chart-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--c-navy);
  letter-spacing: -0.01em;
}

.chart-period {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(13, 44, 109, 0.05);
  color: var(--c-navy);
  font-size: 0.82rem;
  font-weight: 600;
  border: 1px solid rgba(13, 44, 109, 0.08);
}

.period-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--c-gold);
}

.chart-canvas-wrap {
  padding: 22px 24px 26px;
  height: 460px;
  position: relative;
}

/* ───────────────────────── RESPONSIVE ───────────────────────── */
@media (max-width: 900px) {
  .header-inner {
    padding: 0 20px;
  }

  .page-title {
    font-size: 1.1rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .kpi-grid {
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 14px;
  }

  .kpi-card {
    padding: 16px 16px 18px;
  }

  .kpi-value {
    font-size: 1.75rem;
  }

  .chart-canvas-wrap {
    height: 380px;
    padding: 16px 14px 20px;
  }

  .chart-card__head {
    padding: 18px 18px 14px;
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 520px) {
  .page-date {
    display: none;
  }

  .page-title {
    font-size: 1.05rem;
  }

  .refresh-btn span {
    display: none;
  }

  .refresh-btn {
    padding: 6px 9px;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .kpi-icon {
    width: 36px;
    height: 36px;
    border-radius: 9px;
  }

  .kpi-icon svg {
    width: 18px;
    height: 18px;
  }

  .kpi-value {
    font-size: 1.55rem;
  }

  .kpi-label {
    font-size: 0.82rem;
  }

  .chart-canvas-wrap {
    height: 320px;
  }
}

@media (max-width: 380px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

/* Respect users who prefer reduced motion */
@media (prefers-reduced-motion: reduce) {

  .kpi-card,
  .chart-card {
    animation: none;
  }

  .eyebrow-dot {
    animation: none;
  }

  .refresh-btn svg.is-spinning {
    animation: none;
  }
}
</style>